import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Pencil, Plus, Trash2, XCircle } from 'lucide-react';
import {
  TicketStatus,
  type JobEditView,
  type TicketSummary,
  ticketPriorityLabelKey,
  ticketStatusLabelKey,
} from '@nbh/shared';
import { adminApi } from '../../lib/adminApi';
import { api } from '../../lib/api';
import { DeleteDialog } from './SuperAdminDialogs';
import { RetireDialog } from './RetireDialog';
import { SuperAdminJobForm, flattenPlaces, type PlaceOption } from './SuperAdminJobForm';

type Acting = { job: TicketSummary; kind: 'cancel' | 'delete' } | null;

/** The filter chips. `all` is not a status, hence the union rather than TicketStatus. */
type Filter = 'all' | TicketStatus;

const FILTERS: Filter[] = ['all', TicketStatus.Open, TicketStatus.InProgress, TicketStatus.Closed];

/**
 * Job lifecycle on the root tier (decision D27).
 *
 * A job is a ticket. This tab does not show a second kind of work — it shows the same
 * queue technicians see under "Jobs", with the four acts the tier alone may take:
 * raising work directly, correcting it, cancelling it, and deleting it outright.
 *
 * Cancel is offered before Delete and reads as the normal choice, for the same reason
 * retirement outranks deletion everywhere else on this tier: cancelling keeps the row,
 * its history and its chat, and only stops the job counting as work. Delete is last,
 * icon-only, behind a divider, and takes the reference typed out.
 */
export function SuperAdminJobManager() {
  const { t } = useTranslation();
  const [jobs, setJobs] = useState<TicketSummary[] | null>(null);
  const [places, setPlaces] = useState<PlaceOption[]>([]);
  const [staff, setStaff] = useState<{ id: string; name: string }[]>([]);
  const [filter, setFilter] = useState<Filter>('all');
  const [creating, setCreating] = useState(false);
  const [acting, setActing] = useState<Acting>(null);
  /** The job being corrected, once its editable view has arrived. */
  const [editing, setEditing] = useState<JobEditView | null>(null);
  /** Which row is mid-fetch, so its button can say so rather than appear dead. */
  const [opening, setOpening] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    api
      .listTickets({})
      .then(setJobs)
      .catch((caught: Error) => setError(caught.message));
  }, []);

  useEffect(load, [load]);

  // Loaded once, not per dialog open: the form needs both lists the moment it appears,
  // and fetching them on open puts a spinner between the tap and the fields.
  useEffect(() => {
    // Same cross-tier-looking call as SuperAdminRooms: `admin/locations` is a root-tier
    // endpoint now, so this screen reads it directly rather than by inheritance (D23).
    void adminApi.locations
      .tree()
      .then((tree) => setPlaces(flattenPlaces(tree)))
      .catch(() => undefined);
    void adminApi.superAdmin.users
      .list()
      .then((rows) => setStaff(rows.filter((row) => row.isActive)))
      .catch(() => undefined);
  }, []);

  const shown = useMemo(
    () => (filter === 'all' ? (jobs ?? []) : (jobs ?? []).filter((job) => job.status === filter)),
    [jobs, filter],
  );

  /**
   * Fetches the job before opening the form.
   *
   * The queue row cannot prefill the form: it carries `locationName` and `assigneeName`,
   * and a `<select>` needs the id. It has no description and no deadline at all. So the
   * form is opened only once the real values are in hand — showing it first and filling
   * it a moment later would let an operator start typing into fields about to be
   * overwritten.
   */
  const handleEditClick = async (job: TicketSummary) => {
    setOpening(job.id);
    setError(null);
    try {
      setEditing(await adminApi.superAdmin.jobs.get(job.id));
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : t('superAdmin.failed'));
    } finally {
      setOpening(null);
    }
  };

  const done = () => {
    setCreating(false);
    setActing(null);
    setEditing(null);
    load();
  };

  if (!jobs) {
    return error ? (
      <p role="alert" className="text-sm text-red-400">
        {error}
      </p>
    ) : (
      <p className="text-sm text-slate-400">{t('superAdmin.loading')}</p>
    );
  }

  return (
    <div className="space-y-3">
      <button
        type="button"
        onClick={() => setCreating(true)}
        className="flex min-h-[48px] w-full items-center justify-center gap-2 rounded-lg bg-brookfields-green ring-1 ring-brookfields-gold/70 text-sm font-semibold text-white"
      >
        <Plus className="size-4" aria-hidden="true" />
        {t('superAdmin.jobs.create')}
      </button>

      <StatusFilters active={filter} onChange={setFilter} />

      {error && (
        <p role="alert" className="text-sm text-red-400">
          {error}
        </p>
      )}

      {shown.length === 0 ? (
        <p className="text-sm text-slate-500">{t('superAdmin.jobs.empty')}</p>
      ) : (
        <ul className="rounded-xl border border-slate-800">
          {shown.map((job) => (
            <JobRow
              key={job.id}
              job={job}
              opening={opening === job.id}
              onEdit={() => void handleEditClick(job)}
              onAct={(kind) => setActing({ job, kind })}
            />
          ))}
        </ul>
      )}

      <JobDialogs
        creating={creating}
        editing={editing}
        acting={acting}
        places={places}
        staff={staff}
        onDone={done}
        onDismiss={() => {
          setCreating(false);
          setEditing(null);
          setActing(null);
        }}
      />
    </div>
  );
}

/**
 * Every modal this tab can raise, in one place.
 *
 * Grouped because they are mutually exclusive and because the alternative is 60 lines of
 * conditional JSX in the middle of the list, where the list is the thing worth reading.
 */
function JobDialogs({
  creating,
  editing,
  acting,
  places,
  staff,
  onDone,
  onDismiss,
}: {
  creating: boolean;
  editing: JobEditView | null;
  acting: Acting;
  places: PlaceOption[];
  staff: { id: string; name: string }[];
  onDone: () => void;
  onDismiss: () => void;
}) {
  if (creating || editing) {
    return (
      <SuperAdminJobForm
        job={editing}
        places={places}
        staff={staff}
        onSaved={onDone}
        onCancel={onDismiss}
      />
    );
  }

  if (acting?.kind === 'cancel') {
    // Reused as-is: cancelling asks exactly what retiring asks — why.
    return (
      <RetireDialog
        subject={`#${acting.job.reference} ${acting.job.title}`}
        onConfirm={async (reason) => {
          await adminApi.superAdmin.jobs.setStatus(acting.job.id, {
            status: TicketStatus.Cancelled,
            reason,
          });
          onDone();
        }}
        onCancel={onDismiss}
      />
    );
  }

  if (acting?.kind === 'delete') {
    return (
      <DeleteDialog
        subject={`#${acting.job.reference} ${acting.job.title}`}
        // The reference is what staff say over the radio, so it is what they are asked
        // to type — a title can be long, ambiguous, and copied by muscle memory.
        confirmValue={String(acting.job.reference)}
        explainKey="superAdmin.delete.explainJob"
        onConfirm={async (reason, confirm) => {
          await adminApi.superAdmin.jobs.remove(acting.job.id, { reason, confirm });
          onDone();
        }}
        onCancel={onDismiss}
      />
    );
  }

  return null;
}

function StatusFilters({ active, onChange }: { active: Filter; onChange: (next: Filter) => void }) {
  const { t } = useTranslation();

  return (
    <div className="flex flex-wrap gap-2" role="group" aria-label={t('superAdmin.jobs.filter')}>
      {FILTERS.map((name) => (
        <button
          key={name}
          type="button"
          aria-pressed={active === name}
          onClick={() => onChange(name)}
          className={`min-h-[48px] rounded-full px-4 text-xs font-medium ${
            active === name
              ? 'bg-brookfields-green ring-1 ring-brookfields-gold/70 text-white'
              : 'bg-slate-800 text-slate-300'
          }`}
        >
          {name === 'all' ? t('superAdmin.jobs.allJobs') : t(ticketStatusLabelKey[name])}
        </button>
      ))}
    </div>
  );
}

function JobRow({
  job,
  opening,
  onEdit,
  onAct,
}: {
  job: TicketSummary;
  opening: boolean;
  onEdit: () => void;
  onAct: (kind: 'cancel' | 'delete') => void;
}) {
  const { t } = useTranslation();
  const cancelled = job.status === TicketStatus.Cancelled;

  return (
    <li className="flex flex-wrap items-center gap-x-2 border-b border-slate-800 px-3 py-1 last:border-b-0">
      <span className="flex min-w-0 basis-full items-center gap-2 py-2 sm:flex-1 sm:basis-auto">
        <span className="shrink-0 text-xs text-slate-500">#{job.reference}</span>
        <span className={`truncate text-sm ${cancelled ? 'text-slate-600' : 'text-slate-100'}`}>
          {job.title}
        </span>
        <span className="shrink-0 text-xs text-slate-500">
          {t(ticketStatusLabelKey[job.status])} · {t(ticketPriorityLabelKey[job.priority])}
        </span>
      </span>

      <button
        type="button"
        onClick={onEdit}
        disabled={opening}
        aria-label={t('superAdmin.jobs.edit', { name: job.title })}
        className="min-h-[48px] shrink-0 rounded bg-transparent px-2 text-slate-300 disabled:opacity-40"
      >
        <Pencil className={`size-4 ${opening ? 'animate-pulse' : ''}`} aria-hidden="true" />
      </button>

      {/* Offered only while there is something to call off. */}
      {!cancelled && (
        <button
          type="button"
          onClick={() => onAct('cancel')}
          className="flex min-h-[48px] shrink-0 items-center gap-1 rounded bg-transparent px-2 text-xs text-amber-300"
        >
          <XCircle className="size-4" aria-hidden="true" />
          {t('superAdmin.jobs.cancel')}
        </button>
      )}

      {/*
        Last, icon-only, behind a divider. Cancel and Delete must not read as two
        equivalent choices under a thumb: one is reversible in the record, the other
        removes the job and every message on it.
      */}
      <button
        type="button"
        onClick={() => onAct('delete')}
        aria-label={t('superAdmin.jobs.delete', { name: job.title })}
        className="ml-1 min-h-[48px] shrink-0 border-l border-slate-800 bg-transparent px-3 text-red-500"
      >
        <Trash2 className="size-4" aria-hidden="true" />
      </button>
    </li>
  );
}
